import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Typography,
  Paper,
  Chip,
  alpha,
  useTheme,
  Avatar,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Header from "../../components/Header";
import { API_BASE_URL } from "../../config/apiBase";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { tokens } from "../../theme";

const RenterGetById = () => {
  const { id } = useParams();
  const [renter, setRenter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const theme = useTheme();
  
  const baseColors = tokens(theme.palette.mode);

  const colors = {
    ...baseColors,
    blueAccent: {
      100: "#d9eefb",
      200: "#b3ddf7",
      300: "#8dcbf4",
      400: "#67baf0",
      500: "#22a3f1",
      600: "#1b82c1",
      700: "#146291",
      800: "#0e4160",
      900: "#072030",
    },
  };

  useEffect(() => {
    const fetchRenter = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/renter/getRenter/${id}`
        );
        if (response.data.success && response.data.renter) {
          setRenter(response.data.renter);
        } else {
          throw new Error("Failed to fetch renter data");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRenter();
  }, [id]);

  const formatField = (field) => {
    if (field === null || field === undefined) return "N/A";
    if (typeof field === "boolean") return field ? "Yes" : "No";
    return field;
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderField = (key, value) => {
    // Skip password and notification fields
    if (key === "password" || key === "notificationToken" || key === "otp" || key === "otpExpiryTime" || key === "fcmToken") return null;

    // Format the field label
    const label = key
      .replace(/([A-Z])/g, " $1")
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

    return (
      <React.Fragment key={key}>
        <Grid item xs={12} md={3}>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{ 
              borderLeft: `3px solid ${colors.blueAccent[500]}`,
              pl: 1.5,
              lineHeight: 1.5,
              color: colors.blueAccent[500]
            }}
          >
            {label}
          </Typography>
        </Grid>
        <Grid item xs={12} md={9}>
          {key.includes("imageURL") || key.includes("Licence") ? (
            value ? (
              <Box sx={{ mt: 1, mb: 1 }}>
                <Paper elevation={3} sx={{ 
                  padding: 1, 
                  display: 'inline-block', 
                  background: '#ffffff',
                  border: `1px solid #e0e0e0`,
                  borderRadius: 2,
                }}>
                  <img
                    src={value}
                    alt={label}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "200px",
                      objectFit: "contain",
                      borderRadius: "8px",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                    }}
                  />
                </Paper>
              </Box>
            ) : (
              <Chip 
                label="No Image Available" 
                variant="outlined" 
                color="default"
                sx={{ mt: 1 }}
              />
            )
          ) : key === "createdAt" || key === "updatedAt" || key === "birthday" ? (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <CalendarTodayIcon sx={{ mr: 1, color: colors.blueAccent[500], fontSize: 20 }} />
              <Typography variant="body1">{formatDate(value)}</Typography>
            </Box>
          ) : key === "vipStatus" || key === "licenceVerified" || key === "isRegistered" || key.startsWith("is") ? (
            <Chip
              icon={value ? <CheckCircleIcon /> : <CancelIcon />}
              label={value ? "Yes" : "No"}
              color={value ? "success" : "error"}
              variant="filled"
              sx={{ fontWeight: "medium", mt: 1 }}
            />
          ) : (
            <Typography variant="body1" sx={{ mt: 1 }}>{formatField(value)}</Typography>
          )}
        </Grid>
        <Grid item xs={12}>
          <Divider sx={{ 
            my: 2,
            opacity: 0.6,
            borderColor: '#e0e0e0'
          }} />
        </Grid>
      </React.Fragment>
    );
  };

  return (
    <Box m="20px" height="80vh" overflow="auto" paddingRight="20px">
      {/* Header Section - Matching Dashboard Style */}
      <Box
        mb={3}
        p={3}
        borderRadius={2}
        sx={{
          background: `linear-gradient(90deg, ${colors.blueAccent[500]} 0%, ${colors.blueAccent[400]} 100%)`,
          boxShadow: `0 4px 12px rgba(0, 0, 0, 0.1)`,
          border: `1px solid rgba(255, 255, 255, 0.3)`,
        }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <PersonOutlineIcon sx={{ color: "white", fontSize: 28 }} />
          <Typography variant="h3" fontWeight="bold" color="white">
            Renter Details #{id}
          </Typography>
        </Box>
        <Typography variant="body1" color="white">
          View detailed information about the renter
        </Typography>
      </Box>

      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="50vh"
        >
          <CircularProgress color="primary" thickness={4} />
        </Box>
      ) : error ? (
        <Paper elevation={2} sx={{ 
          p: 3, 
          mt: 2, 
          textAlign: 'center', 
          borderLeft: '4px solid #f44336',
          borderRadius: 2,
          backgroundColor: "#ffffff",
          boxShadow: `0 10px 30px rgba(0, 0, 0, 0.05)`,
          border: `1px solid #e0e0e0`,
        }}>
          <Typography color="error" align="center" variant="h6">
            {error}
          </Typography>
        </Paper>
      ) : (
        <Card sx={{ 
          mt: 4, 
          mb: 4, 
          borderRadius: '12px',
          boxShadow: `0 10px 30px rgba(0, 0, 0, 0.05)`,
          border: `1px solid #e0e0e0`,
          overflow: 'hidden',
          backgroundColor: "#ffffff",
        }}>
          {renter?.imageURL && (
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: 3,
              background: `linear-gradient(90deg, ${colors.blueAccent[500]} 0%, ${colors.blueAccent[400]} 100%)`,
              color: 'white'
            }}>
              <Avatar 
                src={renter.imageURL} 
                alt={renter.name || "Renter"}
                sx={{ 
                  width: 100, 
                  height: 100, 
                  border: '4px solid white',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}
              />
              <Typography variant="h5" sx={{ mt: 2, fontWeight: 'bold' }}>
                {renter.name || `Renter #${id}`}
              </Typography>
            </Box>
          )}
          <CardContent sx={{ padding: 4 }}>
            <Grid container spacing={3}>
              {renter &&
                Object.entries(renter).map(([key, value]) =>
                  renderField(key, value)
                )}
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default RenterGetById;